import { getPropertyDetails } from '@/queries/cached'
import { getBestImageUrl } from '@/utils/imageUtils'
import type { Metadata } from 'next'
import { getTranslations } from 'next-intl/server'
import { Message, OtherPhoto } from '@partment/types'

const METADATA_BASE_URL = new URL('https://partment.co')
// Fully-qualified URL on purpose: Next's metadata resolver only leaves a URL
// untouched when it already has a protocol. A root-relative path would be
// path.join-ed onto metadataBase and, because static assets live under the
// `/stays` basePath, would resolve to a 404. This points at a real 1200x630
// card in public/images (served at /stays/images/default-og-image.jpg).
export const DEFAULT_OG_IMAGE =
  'https://partment.co/stays/images/default-og-image.jpg'
const TITLE_TEMPLATE = '%s | Partment'

// --------------------
// Helpers
// --------------------

export const limitText = (text: string, min = 50, max = 160) => {
  let trimmed = text.trim()

  if (trimmed.length > max) {
    trimmed = trimmed.substring(0, max).trim()
    trimmed = trimmed.substring(0, trimmed.lastIndexOf(' ')).trim() + '...'
  }

  if (trimmed.length < min) {
    trimmed +=
      ' Discover more details, photos, and availability on Partment.'.substring(
        0,
        min - trimmed.length,
      )
  }

  return trimmed
}
// Slugify logic
export const toSlug = (value = '') => {
  return value
    .toLowerCase()
    .trim()
    // remove special characters except spaces and hyphens
    .replaceAll(/[^\w\s-]/g, '')
    // replace spaces with hyphens
    .replaceAll(/\s+/g, '-')

    // collapse multiple hyphens into one (safety)
    .replaceAll(/-+/g, '-')
}

type PropertyMetadataParams = {
  city: string
  slug: string
  locale: string
  checkin?: string
  checkout?: string
}

type PropertyMetadataContext = {
  citySlug: string
  canonicalPath: string
  canonicalUrl: string
  locale: string
}

// Site is English-only; advertise a single OG locale so we don't signal a
// non-existent Arabic version to search engines / AI crawlers.
const OG_LOCALE = 'en_US'

const buildPropertyMetadataContext = ({ city, slug, locale }: PropertyMetadataParams): PropertyMetadataContext => {
  const citySlug = toSlug(city)
  const canonicalPath = `/stays/${citySlug}/${slug}`

  return {
    citySlug,
    canonicalPath,
    canonicalUrl: `https://partment.co${canonicalPath}`,
    locale,
  }
}

const buildRobotsMetadata = () => ({
  index: true,
  follow: true,
  googleBot: {
    index: true,
    follow: true,
    'max-video-preview': -1,
    'max-image-preview': 'large' as const,
    'max-snippet': -1,
  },
})

const getPropertyAmenities = (property: Message) => (
  property.property_amenities
    .slice(0, 3)
    .map((item) => item.amenity_display_name)
    .join(', ') || 'modern amenities and comfortable features'
)

const getPropertyOgImage = (property: Message) => {
  const images = property.other_photos?.map((img: OtherPhoto) => getBestImageUrl(img)) || []

  return property.main_photo || images[0] || DEFAULT_OG_IMAGE
}

const getPropertyForMetadata = async ({ slug, checkin, checkout }: PropertyMetadataParams) => {
  try {
    const res = await getPropertyDetails(slug, checkin, checkout)
    return res?.data?.data ?? null
  } catch {
    return null
  }
}

const buildFallbackPropertyMetadata = (
  context: PropertyMetadataContext,
  fallbackTitle: string,
  fallbackDescription: string,
): Metadata => ({
  title: {
    default: fallbackTitle,
    template: TITLE_TEMPLATE,
  },
  description: fallbackDescription,
  metadataBase: METADATA_BASE_URL,
  alternates: {
    canonical: context.canonicalPath,
  },
  openGraph: {
    type: 'article',
    locale: OG_LOCALE,
    url: context.canonicalUrl,
    siteName: 'Partment',
    title: fallbackTitle,
    description: fallbackDescription,
    images: [
      {
        url: DEFAULT_OG_IMAGE,
        width: 1200,
        height: 630,
      },
    ],
  },
  twitter: {
    card: 'summary_large_image',
    title: fallbackTitle,
    description: fallbackDescription,
    images: [DEFAULT_OG_IMAGE],
  },
  robots: buildRobotsMetadata(),
})

const buildResolvedPropertyMetadata = (
  property: Message,
  context: PropertyMetadataContext,
  dynamicTitle: string,
  dynamicDescription: string,
): Metadata => ({
  title: {
    default: dynamicTitle,
    template: TITLE_TEMPLATE,
  },
  description: dynamicDescription,
  metadataBase: METADATA_BASE_URL,
  alternates: {
    canonical: context.canonicalPath,
  },
  openGraph: {
    type: 'article',
    locale: OG_LOCALE,
    url: context.canonicalUrl,
    siteName: 'Partment',
    title: dynamicTitle,
    description: dynamicDescription,
    images: [
      {
        url: getPropertyOgImage(property),
        width: 1200,
        height: 630,
        alt: property.name,
      },
    ],
  },
  twitter: {
    card: 'summary_large_image',
    title: dynamicTitle,
    description: dynamicDescription,
    images: [getPropertyOgImage(property)],
  },
  robots: buildRobotsMetadata(),
})
// --------------------
// Metadata Builder
// --------------------

export async function buildPropertyMetadata(
  params: PropertyMetadataParams,
): Promise<Metadata> {
  const t = await getTranslations('metadata.propertydetails')
  const property = await getPropertyForMetadata(params)
  const fallbackTitle = t('fallbackTitle')
  const fallbackDescription = t('fallbackDescription')
  const context = buildPropertyMetadataContext(params)

  if (!property) {
    return buildFallbackPropertyMetadata(
      context,
      fallbackTitle,
      fallbackDescription,
    )
  }

  const dynamicTitle = t('title', { name: property.name, city: context.citySlug })

  const dynamicDescription = limitText(
    t('dynamicDescriptionWithAmenities', {
      name: property.name,
      city: context.citySlug,
      amenities: getPropertyAmenities(property),
    }),
    50,
    160,
  )

  return buildResolvedPropertyMetadata(
    property,
    context,
    dynamicTitle,
    dynamicDescription,
  )
}
